/**
 * PGLP | MONTE HERMOM
 * COMPONENTE: PrazoAcoesCriticas
 * ------------------------------------------------------------
 * Painel de botões de ação quando o nível de risco é
 * RISCO, CRITICO ou CONSUMIDO.
 * ============================================================
 */

import React, { useState } from 'react';
import {
  AlertTriangle, ShieldAlert, XCircle, ArrowRight,
  FileText, Clock, Ban, CheckCircle2, Loader2,
} from 'lucide-react';

export default function PrazoAcoesCriticas({
  estado,
  onRegistrarProvidencia,
  onPrepararProrrogacao,
}) {
  const [modal, setModal] = useState(null); // 'IMPOSSIBILIDADE' | 'ACESSO_PARCIAL' | 'PRORROGACAO'
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(null);

  if (!estado) return null;

  const risco = estado.nivel_risco;
  if (risco !== 'RISCO' && risco !== 'CRITICO' && risco !== 'CONSUMIDO') return null;

  const corPainel = risco === 'CONSUMIDO'
    ? 'bg-slate-500/5 border-slate-500/30'
    : risco === 'CRITICO'
      ? 'bg-rose-500/5 border-rose-500/30'
      : 'bg-orange-500/5 border-orange-500/30';

  const corTexto = risco === 'CONSUMIDO'
    ? 'text-slate-400'
    : risco === 'CRITICO'
      ? 'text-rose-400'
      : 'text-orange-400';

  const Icone = risco === 'CONSUMIDO' ? XCircle : risco === 'CRITICO' ? ShieldAlert : AlertTriangle;

  const enviarProvidencia = async (acao, payload) => {
    setEnviando(true);
    setSucesso(null);
    const ok = await onRegistrarProvidencia(acao, payload);
    setEnviando(false);
    if (ok) {
      setSucesso(acao);
      setModal(null);
      setTimeout(() => setSucesso(null), 3000);
    }
  };

  return (
    <>
      <div className={`rounded-2xl border px-5 py-4 ${corPainel} space-y-3`}>

        <div className="flex items-start gap-3">
          <Icone size={16} className={`${corTexto} shrink-0 mt-0.5`} />
          <div className="flex-1">
            <p className={`text-[11px] font-black uppercase italic tracking-tight ${corTexto}`}>
              {risco === 'CONSUMIDO' && 'Prazo consumido — ação necessária'}
              {risco === 'CRITICO' && 'Prazo crítico — margem comprometida'}
              {risco === 'RISCO' && 'Prazo em risco — avalie ações'}
            </p>
            <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
              {risco === 'CONSUMIDO' && 'O prazo operacional chegou ao fim. Providência obrigatória.'}
              {risco === 'CRITICO' && 'A margem disponível é mínima. Considere registrar impossibilidade de acesso, acesso parcial ou peticionar prorrogação.'}
              {risco === 'RISCO' && 'A margem caiu abaixo do recomendado. As próximas fases podem ser comprometidas.'}
            </p>
          </div>
        </div>

        {/* BOTÕES */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-2">

          <button
            type="button"
            onClick={() => setModal('IMPOSSIBILIDADE')}
            disabled={enviando}
            className="flex items-center justify-center gap-2 py-3 rounded-xl bg-rose-500/20 border border-rose-500/30 text-rose-300 hover:bg-rose-500/30 disabled:opacity-50 text-[10px] font-black uppercase tracking-widest transition-all"
          >
            <Ban size={13} />
            Impossibilidade
          </button>

          <button
            type="button"
            onClick={() => setModal('ACESSO_PARCIAL')}
            disabled={enviando}
            className="flex items-center justify-center gap-2 py-3 rounded-xl bg-orange-500/20 border border-orange-500/30 text-orange-300 hover:bg-orange-500/30 disabled:opacity-50 text-[10px] font-black uppercase tracking-widest transition-all"
          >
            <ShieldAlert size={13} />
            Acesso Parcial
          </button>

          <button
            type="button"
            onClick={() => setModal('PRORROGACAO')}
            disabled={enviando}
            className="flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-300 hover:bg-amber-500/30 disabled:opacity-50 text-[10px] font-black uppercase tracking-widest transition-all"
          >
            <Clock size={13} />
            Prorrogação
          </button>

        </div>

        {sucesso && (
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-emerald-400 pt-1">
            <CheckCircle2 size={12} />
            Providência registrada com sucesso
          </div>
        )}
      </div>

      {/* MODAIS */}
      {modal === 'IMPOSSIBILIDADE' && (
        <ModalImpossibilidade
          enviando={enviando}
          onEnviar={(payload) => enviarProvidencia('IMPOSSIBILIDADE_ACESSO', payload)}
          onFechar={() => setModal(null)}
        />
      )}

      {modal === 'ACESSO_PARCIAL' && (
        <ModalAcessoParcial
          enviando={enviando}
          onEnviar={(payload) => enviarProvidencia('ACESSO_PARCIAL', payload)}
          onFechar={() => setModal(null)}
        />
      )}

      {modal === 'PRORROGACAO' && (
        <ModalProrrogacao
          estado={estado}
          enviando={enviando}
          onEnviar={(prorrogacao) => onPrepararProrrogacao(prorrogacao)}
          onFechar={() => setModal(null)}
        />
      )}
    </>
  );
}

/* ============================================================
   MODAIS
   ============================================================ */

function ModalWrapper({ titulo, subtitulo, icone: Icone, cor, children, onFechar }) {
  return (
    <div className="fixed inset-0 z-[410] bg-slate-950/95 backdrop-blur-xl flex items-center justify-center p-4">
      <div className="bg-slate-900 w-full max-w-lg rounded-3xl border border-white/10 shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="bg-slate-950 p-5 flex justify-between items-center border-b border-white/5 shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cor.bg} ${cor.border} border`}>
              <Icone size={18} className={cor.text} />
            </div>
            <div>
              <h3 className="text-white text-[14px] font-black uppercase italic tracking-tight leading-none">
                {titulo}
              </h3>
              <p className="text-[8px] text-slate-500 font-black uppercase tracking-widest mt-1">
                {subtitulo}
              </p>
            </div>
          </div>
          <button
            onClick={onFechar}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-all"
          >
            ✕
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {children}
        </div>
      </div>
    </div>
  );
}

function ModalImpossibilidade({ enviando, onEnviar, onFechar }) {
  const [motivo, setMotivo] = useState('');
  const [tentativas, setTentativas] = useState('');

  const valido = motivo.trim().length >= 10;

  return (
    <ModalWrapper
      titulo="Registrar Impossibilidade de Acesso"
      subtitulo="Exceção 8 — Documentação de diligências"
      icone={Ban}
      cor={{ bg: 'bg-rose-500/10', border: 'border-rose-500/30', text: 'text-rose-400' }}
      onFechar={onFechar}
    >
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
            Motivo (mínimo 10 caracteres) *
          </span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            placeholder="Ex: Tentativas por WhatsApp, e-mail e telefone sem retorno após 5 dias úteis."
            className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-400 resize-none placeholder:text-slate-600"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
            Resumo das Tentativas (opcional)
          </span>
          <input
            type="text"
            value={tentativas}
            onChange={(e) => setTentativas(e.target.value)}
            placeholder="Ex: 3 WhatsApp, 2 e-mails, 5 ligações"
            className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-400 placeholder:text-slate-600"
          />
        </label>

        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3 flex gap-2">
          <AlertTriangle size={12} className="text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[9px] text-amber-300 leading-relaxed">
            Ao registrar, o laudo será marcado com <strong>possui_impossibilidade_acesso = 1</strong>.
            Use essa ação somente após esgotar tentativas razoáveis.
          </p>
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          onClick={onFechar}
          className="flex-1 py-3 rounded-xl bg-white/5 text-slate-400 hover:bg-white/10 text-[10px] font-black uppercase tracking-widest transition-all"
        >
          Cancelar
        </button>
        <button
          disabled={!valido || enviando}
          onClick={() => onEnviar({ motivo, tentativas })}
          className="flex-1 py-3 rounded-xl bg-rose-500 text-white hover:bg-rose-400 disabled:opacity-30 disabled:cursor-not-allowed text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2"
        >
          {enviando ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
          Registrar
        </button>
      </div>
    </ModalWrapper>
  );
}

function ModalAcessoParcial({ enviando, onEnviar, onFechar }) {
  const [motivo, setMotivo] = useState('');
  const [areas, setAreas] = useState('');

  const valido = motivo.trim().length >= 10;

  return (
    <ModalWrapper
      titulo="Registrar Acesso Parcial"
      subtitulo="Delimitação de vistoria"
      icone={ShieldAlert}
      cor={{ bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400' }}
      onFechar={onFechar}
    >
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
            Motivo do acesso parcial *
          </span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            placeholder="Ex: Ocupante autorizou apenas vistoria externa. Interiores não acessíveis."
            className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-400 resize-none placeholder:text-slate-600"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
            Áreas vistoriadas / Áreas não acessíveis
          </span>
          <input
            type="text"
            value={areas}
            onChange={(e) => setAreas(e.target.value)}
            placeholder="Ex: Fachada, quintal / Interiores, garagem"
            className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-400 placeholder:text-slate-600"
          />
        </label>

        <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-3 flex gap-2">
          <AlertTriangle size={12} className="text-orange-400 shrink-0 mt-0.5" />
          <p className="text-[9px] text-orange-300 leading-relaxed">
            Ao registrar, o laudo será marcado com <strong>possui_acesso_parcial = 1</strong>
            e o perito deverá fazer ressalvas explícitas no laudo final.
          </p>
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          onClick={onFechar}
          className="flex-1 py-3 rounded-xl bg-white/5 text-slate-400 hover:bg-white/10 text-[10px] font-black uppercase tracking-widest transition-all"
        >
          Cancelar
        </button>
        <button
          disabled={!valido || enviando}
          onClick={() => onEnviar({ motivo, areas })}
          className="flex-1 py-3 rounded-xl bg-orange-500 text-white hover:bg-orange-400 disabled:opacity-30 disabled:cursor-not-allowed text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2"
        >
          {enviando ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
          Registrar
        </button>
      </div>
    </ModalWrapper>
  );
}

function ModalProrrogacao({ estado, enviando, onEnviar, onFechar }) {
  const [tipo, setTipo] = useState('DIAS_UTEIS');
  const [quantidade, setQuantidade] = useState('5');
  const [novaData, setNovaData] = useState('');
  const [documento, setDocumento] = useState('');
  const [observacao, setObservacao] = useState('');

  const valido = tipo === 'DATA_LIMITE'
    ? novaData.trim() !== ''
    : parseInt(quantidade, 10) > 0;

  const enviar = () => {
    const payload = {
      tipo,
      documento,
      observacao,
      ...(tipo === 'DATA_LIMITE'
        ? { nova_data: novaData }
        : { quantidade_dias: parseInt(quantidade, 10) }),
    };
    onEnviar(payload);
  };

  return (
    <ModalWrapper
      titulo="Preparar Prorrogação"
      subtitulo="Exceção 2 — Prorrogação concedida pelo juiz"
      icone={Clock}
      cor={{ bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400' }}
      onFechar={onFechar}
    >
      <div className="space-y-3">

        <div className="bg-slate-950 rounded-xl p-3">
          <div className="flex justify-between text-[10px] font-mono">
            <span className="text-slate-500">Horas totais atuais:</span>
            <span className="text-white font-bold">{estado.horas_totais}h</span>
          </div>
          <div className="flex justify-between text-[10px] font-mono mt-1">
            <span className="text-slate-500">Data limite atual:</span>
            <span className="text-white font-bold">
              {new Date(estado.data_limite_atual).toLocaleDateString('pt-BR')}
            </span>
          </div>
        </div>

        <label className="block space-y-1">
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
            Tipo de prorrogação
          </span>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-400 cursor-pointer"
          >
            <option value="DIAS_UTEIS">Dias úteis</option>
            <option value="DIAS_CORRIDOS">Dias corridos</option>
            <option value="DATA_LIMITE">Nova data limite específica</option>
          </select>
        </label>

        {tipo === 'DATA_LIMITE' ? (
          <label className="block space-y-1">
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
              Nova data limite *
            </span>
            <input
              type="datetime-local"
              value={novaData}
              onChange={(e) => setNovaData(e.target.value)}
              className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-400"
            />
          </label>
        ) : (
          <label className="block space-y-1">
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
              Quantidade de dias *
            </span>
            <input
              type="number"
              min="1"
              value={quantidade}
              onChange={(e) => setQuantidade(e.target.value)}
              className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-400"
            />
          </label>
        )}

        <label className="block space-y-1">
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
            Referência documental
          </span>
          <input
            type="text"
            value={documento}
            onChange={(e) => setDocumento(e.target.value)}
            placeholder="Ex: Decisão fls. 123 de 01/10/2026"
            className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-400 placeholder:text-slate-600"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
            Observação
          </span>
          <textarea
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            rows={2}
            placeholder="Opcional"
            className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-400 resize-none placeholder:text-slate-600"
          />
        </label>

        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3 flex gap-2">
          <AlertTriangle size={12} className="text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[9px] text-amber-300 leading-relaxed">
            Registro interno para controle. A petição formal deve ser enviada separadamente ao juízo.
          </p>
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          onClick={onFechar}
          className="flex-1 py-3 rounded-xl bg-white/5 text-slate-400 hover:bg-white/10 text-[10px] font-black uppercase tracking-widest transition-all"
        >
          Cancelar
        </button>
        <button
          disabled={!valido || enviando}
          onClick={enviar}
          className="flex-1 py-3 rounded-xl bg-amber-500 text-slate-950 hover:bg-amber-400 disabled:opacity-30 disabled:cursor-not-allowed text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2"
        >
          {enviando ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
          Aplicar
        </button>
      </div>
    </ModalWrapper>
  );
}